import React, { useState, useEffect, useContext } from 'react';
import { KeyRound, Send, Clock, Check, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { AuthContext } from '../App';
import ApiKeysMatrizSection from '../components/ApiKeysMatrizSection';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Página "API Keys" del cliente matriz, accesible desde el menú superior.
 * - Muestra los tokens de su red (ApiKeysMatrizSection se autocarga).
 * - Permite SOLICITAR una API Key a CentralSalud (el admin la aprueba y emite).
 */
export default function MisApiKeys() {
  const { theme } = useTheme();
  const { user } = useContext(AuthContext);
  const tkn = () => localStorage.getItem('token');
  const clienteId = user?.clinica_matriz;

  const [solicitudes, setSolicitudes] = useState([]);
  const [enviando, setEnviando]       = useState(false);
  const [motivo, setMotivo]           = useState('');
  const [form, setForm]               = useState(false);
  const [msg, setMsg]                 = useState(null);

  const bgPrimary   = { backgroundColor: theme.primary };
  const textPrimary = { color: theme.primary };
  const showMsg = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };
  const fecha = (v) => v ? new Date(v).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  const fetchSolicitudes = async () => {
    try {
      const r = await fetch(`${API_URL}/api/api-key-solicitudes`, { headers: { Authorization: `Bearer ${tkn()}` } });
      const d = await r.json();
      setSolicitudes(Array.isArray(d.solicitudes) ? d.solicitudes : []);
    } catch { setSolicitudes([]); }
  };
  useEffect(() => { fetchSolicitudes(); }, []);

  const solicitar = async () => {
    if (!clienteId) { showMsg('Tu usuario no tiene una clínica asociada', 'error'); return; }
    setEnviando(true);
    try {
      const r = await fetch(`${API_URL}/api/clientes/${clienteId}/stock-api-token/solicitar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo: motivo.trim() || undefined }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'No se pudo enviar la solicitud');
      showMsg(d.message || 'Solicitud enviada');
      setForm(false); setMotivo('');
      fetchSolicitudes();
    } catch (e) { showMsg(e.message || 'No se pudo enviar la solicitud', 'error'); }
    finally { setEnviando(false); }
  };

  const pendiente = solicitudes.find(s => s.estado === 'pendiente');
  const ultimaResuelta = solicitudes.find(s => s.estado !== 'pendiente');

  return (
    <div className="min-h-screen bg-gray-50">
      {msg && (
        <div className={`fixed top-20 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${msg.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
          {msg.text}
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Cabecera */}
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2.5 rounded-xl" style={{ backgroundColor: theme.primaryLight }}>
            <KeyRound className="w-6 h-6" style={textPrimary} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
            <p className="text-sm text-gray-500">Tokens de integración para consumir tus datos desde herramientas externas (Excel, BI…)</p>
          </div>
        </div>

        {/* Solicitud */}
        <div className="mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          {pendiente ? (
            <div className="flex items-start gap-3">
              <Clock size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-800 text-sm">Solicitud enviada — en revisión</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Pediste una API Key el {fecha(pendiente.creada_en)}. CentralSalud la revisará y, al aprobarla,
                  aparecerá aquí abajo para que la reclames.
                </p>
              </div>
            </div>
          ) : form ? (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">¿Para qué la vas a usar? (opcional)</label>
              <textarea
                value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
                placeholder="Ej. Integrar nuestros pedidos en Power BI"
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none"
              />
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => { setForm(false); setMotivo(''); }}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50">
                  Cancelar
                </button>
                <button onClick={solicitar} disabled={enviando}
                  className="px-4 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                  style={bgPrimary}>
                  <Send size={14} /> {enviando ? 'Enviando…' : 'Enviar solicitud'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-semibold text-gray-800 text-sm">¿Necesitás una API Key?</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Solicitala y CentralSalud la emitirá. Después la reclamás desde aquí (se muestra una sola vez).
                </p>
                {ultimaResuelta && (
                  <p className="text-xs mt-1 flex items-center gap-1.5"
                     style={{ color: ultimaResuelta.estado === 'aprobada' ? '#059669' : '#dc2626' }}>
                    {ultimaResuelta.estado === 'aprobada' ? <Check size={12} /> : <X size={12} />}
                    Última solicitud {ultimaResuelta.estado} el {fecha(ultimaResuelta.resuelta_en)}
                    {ultimaResuelta.nota_admin && ` — ${ultimaResuelta.nota_admin}`}
                  </p>
                )}
              </div>
              <button onClick={() => setForm(true)}
                className="px-4 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 flex items-center gap-1.5 flex-shrink-0"
                style={bgPrimary}>
                <Send size={14} /> Solicitar API Key
              </button>
            </div>
          )}
        </div>

        {/* Tokens de la red */}
        <ApiKeysMatrizSection />
      </div>
    </div>
  );
}
